import { create } from 'zustand';
import { NV_COMMISSION_TIERS } from '@trinco/shared';
import type { Deal } from '@trinco/shared';


// Compute quarterly commission from a list of signed/active deals using NV tiers
export function calcQuarterlyBonus(signedMrr: number): number {
  const burn = 13500; // current NV monthly burn
  const tiers = NV_COMMISSION_TIERS;
  let monthly = 0;
  let remaining = signedMrr;
  for (const tier of tiers) {
    if (remaining <= 0) break;
    const lo = tier.minRevenue;
    const hi = tier.maxRevenue ?? Infinity;
    const inBand = Math.min(remaining, hi - lo);
    monthly += inBand * tier.rate;
    remaining -= inBand;
  }
  // suppress unused var warning — burn is used conceptually for tier thresholds
  void burn;
  return Math.round(monthly * 3); // quarterly = monthly × 3
}

// Get effective MRR for a deal given its current stage
export function effectiveMrr(deal: Deal, stage?: string): number {
  const s = stage ?? deal.stage;
  const amount = deal.amountCad ?? deal.amount ?? 0;
  const stageWeights: Record<string, number> = {
    lead: 0, prospect: 0, negotiating: 0,
    verbal: 0, signed: 1, active: 1, churned: 0,
  };
  return amount * (stageWeights[s] ?? 0);
}

// moveStage API caller — set externally once trpc is available
let _moveStageApi: ((dealId: string, stage: string) => Promise<void>) | null = null;
export function setMoveStageApi(fn: (dealId: string, stage: string) => Promise<void>) {
  _moveStageApi = fn;
}

interface PipelineState {
  deals: Deal[];
  draggedDealId: string | null;
  optimisticMoves: Map<string, string>;
  lastBonusDelta: number | null;

  setDeals: (deals: Deal[]) => void;
  setDraggedDeal: (id: string | null) => void;
  moveOptimistic: (dealId: string, stageSlug: string) => void;
  commitMove: (dealId: string, stageSlug: string) => void;
  clearOptimistic: (dealId: string) => void;
  clearAll: () => void;
  clearBonusDelta: () => void;
  addDeal: (deal: Deal) => void;
  updateDealAmount: (dealId: string, amount: number) => void;
}

export const usePipelineStore = create<PipelineState>((set, get) => ({
  deals: [],
  draggedDealId: null,
  optimisticMoves: new Map(),
  lastBonusDelta: null,

  setDeals: (deals) => set({ deals }),

  setDraggedDeal: (id) => set({ draggedDealId: id }),

  moveOptimistic: (dealId, stageSlug) =>
    set((state) => {
      const next = new Map(state.optimisticMoves);
      next.set(dealId, stageSlug);
      return { optimisticMoves: next };
    }),

  commitMove: (dealId, stageSlug) => {
    const state = get();
    const deal = state.deals.find((d) => d.id === dealId);
    if (!deal) return;

    // Compute bonus before and after to get delta
    const getSignedMrr = (deals: Deal[], overrides: Map<string, string>) =>
      deals.reduce((sum, d) => {
        const s = overrides.get(d.id) ?? d.stage;
        return sum + effectiveMrr(d, s);
      }, 0);

    const beforeMrr = getSignedMrr(state.deals, new Map());
    const afterOverrides = new Map([[dealId, stageSlug]]);
    const afterMrr = getSignedMrr(state.deals, afterOverrides);
    const delta = calcQuarterlyBonus(afterMrr) - calcQuarterlyBonus(beforeMrr);

    set((s) => {
      const next = new Map(s.optimisticMoves);
      next.delete(dealId);
      return {
        deals: s.deals.map((d) =>
          d.id === dealId ? { ...d, stage: stageSlug, updatedAt: new Date().toISOString() } : d,
        ),
        optimisticMoves: next,
        lastBonusDelta: delta !== 0 ? delta : null,
      };
    });

    // Persist to API
    if (_moveStageApi) {
      _moveStageApi(dealId, stageSlug).catch(() => {
        // Revert on failure
        set((s) => ({
          deals: s.deals.map((d) =>
            d.id === dealId ? { ...d, stage: deal.stage, updatedAt: deal.updatedAt } : d,
          ),
        }));
      });
    }
  },

  clearOptimistic: (dealId) =>
    set((state) => {
      const next = new Map(state.optimisticMoves);
      next.delete(dealId);
      return { optimisticMoves: next };
    }),

  clearAll: () => set({ optimisticMoves: new Map(), draggedDealId: null }),
  clearBonusDelta: () => set({ lastBonusDelta: null }),

  addDeal: (deal) => set((s) => ({ deals: [...s.deals, deal] })),

  updateDealAmount: (dealId, amount) =>
    set((s) => ({
      deals: s.deals.map((d) => d.id === dealId ? { ...d, amount, amountCad: amount } : d),
    })),
}));
